/**
 * StickyChatWidget — LinkedIn-style persistent chat widget (desktop only)
 *
 * Orchestrator that renders:
 * - MinimizedChatToast bars (incoming message notifications, leftmost)
 * - StickyContactTray (conversation list, bottom-right) — always visible
 * - StickyChatWindow (active chat, to the left of tray)
 * - StickyNewMessage (new message panel, to the left of tray)
 *
 * Hidden on mobile and when the /messages route is active (full chat takes over).
 */

import { useLocation } from 'react-router-dom';
import { AnimatePresence } from 'motion/react';
import useMediaQuery, { MD_BREAKPOINT } from '@/hooks/useMediaQuery';
import { StickyContactTray } from './StickyContactTray';
import { StickyChatWindow } from './StickyChatWindow';
import { StickyNewMessage } from './StickyNewMessage';
import { MinimizedChatToast } from './MinimizedChatToast';
import { useChatWidgetStore } from '../store';

export function StickyChatWidget() {
  const isMobile = useMediaQuery(MD_BREAKPOINT);
  const { pathname } = useLocation();
  const { activeConversationId, isNewMessageOpen, messageToasts } = useChatWidgetStore();

  // Hide on mobile or when full messaging view is active
  if (isMobile || pathname.startsWith('/messages')) return null;

  const showChat = !!activeConversationId;

  return (
    <div dir="ltr" className="fixed bottom-0 right-4 z-50 flex items-end gap-2">
      {/* Toast bars — leftmost */}
      <AnimatePresence mode="popLayout">
        {messageToasts.map((toast) => (
          <MinimizedChatToast key={toast.conversationId} toast={toast} />
        ))}
      </AnimatePresence>

      {/* New message panel — to the left of tray */}
      {isNewMessageOpen && !showChat && <StickyNewMessage />}

      {/* Chat window — to the left of tray */}
      {showChat && <StickyChatWindow />}

      {/* Contact tray — always visible (minimized or expanded) */}
      <StickyContactTray />
    </div>
  );
}
